import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { orderBy } from "lodash";
import DatePicker from "@common/components/datePicker";
import Dropdown from "@common/components/dropdown";
import { useTranslation } from "@common/hooks/translation";
import SearchInput from "@common/components/searchInput";
import { normalizeAlias } from "@common/helpers";
import Config from "@skin_config";

const ResultFilters = ({ initialFilters, filters, searchText, onChange, onSearchChange, activeCompetitions }) => {
  const t = useTranslation();

  const sportListOptions = useMemo(
    () =>
      orderBy(
        (Array.isArray(activeCompetitions?.details) ? activeCompetitions?.details : [])
          .map((item) => ({
            value: item.Id,
            name: item.Name,
          }))
          .filter((sport) => !Config.main.sportsIdsBlackList.includes(sport.value)),
        [(sport) => sport.name.toLowerCase()],
        ["asc"]
      ),
    [activeCompetitions]
  );

  const competitionListOptions = useMemo(() => {
    if (!filters.sport_id || !Array.isArray(activeCompetitions?.details)) return [];

    const activeSport = activeCompetitions?.details.find((item) => item.Id === filters.sport_id);

    if (!activeSport) return [];

    return activeSport.Regions.reduce(
      (acc, region) =>
        acc.concat(
          region.Competitions.map((competition) => ({
            value: competition.Id,
            name: (
              <div className="competition-item">
                <div className={`flag icon-${normalizeAlias(region.Alias)}`} />
                <span>{competition.Name}</span>
              </div>
            ),
          }))
        ),
      []
    );
  }, [activeCompetitions, filters]);

  useEffect(() => {
    if (
      sportListOptions.length &&
      (!filters.sport_id || !sportListOptions.find((sport) => sport.value === filters.sport_id))
    ) {
      const soccerSport = sportListOptions.find((sport) => sport.value === 1);

      onChange({ ...filters, sport_id: soccerSport?.value || sportListOptions[0].value });
    }
  }, [sportListOptions, filters, onChange]);

  const changeDate = (selected) => {
    const endOfDay = moment(selected).endOf("day").unix();

    onChange({
      ...filters,
      from_date: endOfDay - 86400,
      to_date: endOfDay,
    });
  };

  const changeSport = (selected) => {
    const newFilters = { ...filters };

    delete newFilters.competition_id;
    newFilters.sport_id = selected.value;

    onChange(newFilters);
  };

  const reset = () => {
    const needToReset =
      filters.sport_id !== sportListOptions[0]?.value ||
      filters.competition_id ||
      searchText ||
      filters.from_date !== initialFilters.from_date ||
      filters.to_date !== initialFilters.to_date;

    if (needToReset) {
      onChange(initialFilters);
      onSearchChange("");
    }
  };

  return (
    <div className="results-page-top">
      <div className="results-filters">
        <div className="results-page-h-left">
          <div className="r-date-picker">
            <DatePicker
              onlyPast
              selected={moment.unix(filters.to_date).toDate()}
              onChange={changeDate}
              dateFilter={Config.main.dateFilter}
            />
          </div>
          <div className="r-d-drop-wrp">
            <Dropdown
              placeholder={t("Sport")}
              value={filters.sport_id}
              options={sportListOptions}
              onChange={changeSport}
            />
          </div>
          <div className="r-d-drop-wrp">
            <Dropdown
              placeholder={t("Competition")}
              value={filters.competition_id}
              options={competitionListOptions}
              onChange={(selected) => onChange({ ...filters, competition_id: selected.value })}
            />
          </div>
          <button type="button" className="r-p-btn-reset" onClick={reset}>
            {t("Reset")}
          </button>
        </div>
        <div className="results-page-h-right">
          <SearchInput value={searchText} onChange={onSearchChange} />
        </div>
      </div>
    </div>
  );
};

ResultFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  initialFilters: PropTypes.object.isRequired,
  searchText: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
};

export default ResultFilters;
